import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Checkbox,
  MenuItem
} from '@mui/material';
import api from "../../store/api/api";
import { CampaignMembership, UserAccount, Campaign } from "../../types/admin";
import { useSelector } from 'react-redux';

interface CampaignMembersTableProps {
  originalMemberships: CampaignMembership[];
  user: UserAccount;
  refreshUserPermissions: (uid: number) => void;
}

export interface MemberPayload {
  campaign_id: number;
  role_id: number;
  user_id: number;
}

const RoleMap: Record<string, number> = { 'viewer': 235, 'manager': 234, 'admin': 45 };

const CampaignMembersTable: React.FC<CampaignMembersTableProps> = ({ originalMemberships = [], user, refreshUserPermissions }) => {

  const allCampaigns = useSelector((state: any) => state.campaigns.data);
  const [myMemberships, setMemberships] = useState<CampaignMembership[]>(originalMemberships);
  const [newCampaignId, setNewCampaignId] = useState<number>(0);
  const shownCampaigns = {}

  useEffect(() => {
    setMemberships(originalMemberships);
  }, [originalMemberships]);

  const getRole = (roleId: number, campaignId: number) => {
    return myMemberships && myMemberships.find((member) => member.role.id === roleId && member.campaign.id === campaignId)
  }

  const handleRoleChange = (roleId: number, campaign_id: number) => {
    const hasRole = getRole(roleId, campaign_id)
    if (hasRole) {
      removeRole(hasRole.id);
    } else {
      addRole(campaign_id, roleId, user.id);
    }
  };

  const addRole = async (campaign_id: number, role_id: number, user_id: number) => {
    const payload: MemberPayload = {
      campaign_id,
      role_id,
      user_id
    };

    try {
      const response = await api.post('/memberships/', payload);
      console.log('Role added:', response.data);
      if (response) {
        setMemberships([...myMemberships, response.data]);
      }
    } catch (error) {
      console.error('Error adding role:', error.response?.data || error.message);
    }
  };

  const removeRole = async (membership_id: number) => {

    try {
      const response = await api.delete(`/memberships/${membership_id}/`);
      console.log('Role removed', response.data);
      if (response) {
        setMemberships(myMemberships.filter((member) => member.id !== membership_id));
      }
    } catch (error) {
      console.error('Error removing role:', error.response?.data || error.message);
    }
  };

  const getActiveRoles = (campaign_id: number) => {
    const roles = [];
    for (let member of myMemberships) {
      if (member.campaign.id === campaign_id) {
        roles.push(<Typography key={member.role.id} variant="body2" color="text.secondary" >{member.role.name}</Typography>)
      }
    }
    if (roles.length === 0) {
      return <Typography variant="body2" color="text.secondary" >-</Typography>
    }
    return roles;
  }

  const makeCampaignSelector = () => {
    return <TextField select
      value={newCampaignId}
      label="Add Campaign Membership"
      fullWidth
      variant="standard"
      onChange={(e) => setNewCampaignId(parseInt(e.target.value))}>
      {allCampaigns.map((campaign: Campaign) => (
        <MenuItem key={campaign.id} value={campaign.id}>
          {campaign.name}
        </MenuItem>
      ))}
    </TextField>
  }

  const makeRoleSelector = (campaign_id: number) => {
    const opts = [];
    const selected = [];
    for (let roleName in RoleMap) {
      const roleId = RoleMap[roleName];
      if (getRole(roleId, campaign_id)) {
        selected.push(roleName);
      }
      opts.push(<MenuItem key={roleId} value={roleId}>
        <Checkbox
          size="small"
          sx={{ p: 0, m: 0 }}
          checked={getRole(roleId, campaign_id) ? true : false}
          onChange={() => handleRoleChange(roleId, campaign_id)}
          color="primary"
        />
        {roleName}
      </MenuItem>)
    }

    return <TextField select
      label={selected.length > 0 ? "Change Roles" : "Add Role"}
      variant="standard"
      fullWidth
      disabled={campaign_id === 0}
      onChange={(e) => handleRoleChange(parseInt(e.target.value), campaign_id)}>
      {opts}
    </TextField>;

  }

  const makeCampaignRow = (campaign: Campaign | null) => {
    const campaignId = campaign ? campaign.id : newCampaignId;
    return <Grid container justifyContent="space-between" alignItems="center" alignContent="center"
      aria-campaign-id={campaignId}
      aria-campaign-label={campaign ? campaign.name : 'Add Campaign Membership'}
      key={`campaign-${campaign ? campaignId : 'new'}`}
      gap={2}
      sx={{ borderBottom: 1, borderColor: 'divider', paddingTop: 1, paddingBottom: 1, paddingLeft: 2, paddingRight: 2 }}
    >
      {campaign ?
        <Grid item style={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" color="primary">
            {campaign.name}
          </Typography>
          {campaign.created_at && <Typography variant="body2" color="text.secondary" >
            {new Date(campaign.created_at).toDateString()}
          </Typography>
          }
          {!campaign.is_active && <Typography variant='caption'>(Inactive <small>#{campaign.id}</small>)</Typography>}
        </Grid>
        : <Grid item style={{ flexGrow: 1 }}>
          {makeCampaignSelector()}
        </Grid>
      }
      <Grid item sx={{ minWidth: '150px', textAlign: 'right' }}>
        {makeRoleSelector(campaignId)}
      </Grid>
      <Grid item sx={{ minWidth: '100px' }}>
        {getActiveRoles(campaignId)}
      </Grid>
    </Grid>
  }

  if (!allCampaigns || allCampaigns.length === 0) {
    return <Typography variant="body1" color="text.secondary" >loading campaigns...</Typography>
  }

  const activeCampaigns = [];
  const inactiveCampaigns = [];
  for (let member of myMemberships) {
    if (!shownCampaigns[member.campaign.id]) {
      shownCampaigns[member.campaign.id] = true;
      if (member.campaign.is_active) {
        activeCampaigns.push(makeCampaignRow(member.campaign));
      } else {
        inactiveCampaigns.push(makeCampaignRow(member.campaign));
      }     
    }
  }

  return (
    <Grid container flexDirection="column" >
      {makeCampaignRow(null)}

      {activeCampaigns}

      {inactiveCampaigns.length > 0 && <React.Fragment>
        <Typography variant="h4" color="primary" sx={{ p: 1 }} >Inactive Campaign Memberships</Typography>
        {inactiveCampaigns}
      </React.Fragment>
      }

    </Grid>
  );
};

export default CampaignMembersTable; 